import { supabase } from '@/lib/supabase';

export interface CollaborativeNote {
  id: string;
  title: string;
  content: unknown;
  archived: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export const NOTE_TITLE_MAX = 200;
// Keep in sync with the DB check constraint (~1MB) in the migration.
export const NOTE_CONTENT_MAX_BYTES = 1048576;

const TABLE = 'collaborative_notes';

export function contentByteSize(content: unknown): number {
  try {
    return new Blob([JSON.stringify(content ?? [])]).size;
  } catch {
    return Number.MAX_SAFE_INTEGER;
  }
}

export async function listNotes(includeArchived = false): Promise<CollaborativeNote[]> {
  let query = supabase
    .from(TABLE)
    .select('*')
    .order('updated_at', { ascending: false });

  if (!includeArchived) {
    query = query.eq('archived', false);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as CollaborativeNote[];
}

export async function getNote(id: string): Promise<CollaborativeNote | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return (data as CollaborativeNote) ?? null;
}

export async function createNote(title: string): Promise<CollaborativeNote> {
  const trimmed = title.trim();
  if (!trimmed) throw new Error('Title is required');

  const { data, error } = await supabase
    .from(TABLE)
    .insert({ title: trimmed.slice(0, NOTE_TITLE_MAX), content: [] })
    .select()
    .single();

  if (error) throw error;
  return data as CollaborativeNote;
}

export async function updateNoteContent(id: string, content: unknown): Promise<CollaborativeNote> {
  if (contentByteSize(content) > NOTE_CONTENT_MAX_BYTES) {
    throw new Error('Note content is too large');
  }

  const { data, error } = await supabase
    .from(TABLE)
    .update({ content })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CollaborativeNote;
}

export async function renameNote(id: string, title: string): Promise<CollaborativeNote> {
  const trimmed = title.trim();
  if (!trimmed) throw new Error('Title is required');

  const { data, error } = await supabase
    .from(TABLE)
    .update({ title: trimmed.slice(0, NOTE_TITLE_MAX) })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CollaborativeNote;
}

export async function setArchived(id: string, archived: boolean): Promise<CollaborativeNote> {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ archived })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CollaborativeNote;
}

export async function deleteNote(id: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq('id', id);
  if (error) throw error;
}
